const PROXY_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
export const API_BASE_URL = PROXY_BASE_URL;

interface Committee {
  id: number;
  name: string;
}

export interface MeetingDocument {
  name: string;
  url: string;
  /** Set by the backend for matter-backed docs — lets the UI link to its own bill page */
  MatterId?: number;
}

export interface LegistarEvent {
  EventId: number;
  EventBodyId: number;
  EventDate: string;
  EventTime: string;
  EventLocation: string;
  EventAgendaStatusName: string;
  EventMinutesStatusName: string;
  EventBodyName: string;
  EventComment?: string | null;
  // Matter deep-links + scannable hearing subjects inlined by /api/events and
  // /api/events/search (codex PF1/PF2 + calendar UX pass)
  Documents?: MeetingDocument[];
  Topics?: string[];
}

export interface Meeting {
  id: number;
  title: string;
  date: string;
  /** Raw ISO EventDate — for upcoming/past logic (date is preformatted for display) */
  rawDate: string;
  time: string;
  location: string;
  description: string;
  committee?: string;
  committeeId?: number;
  comment?: string | null;
  topics: string[];
  documents: MeetingDocument[];
}

// Single source of truth for the LegistarEvent -> Meeting card mapping (used by
// both the meetings list and the search results path in App.tsx).
export const meetingFromEvent = (event: LegistarEvent): Meeting => ({
  id: event.EventId,
  title: `${event.EventBodyName} - ${new Date(event.EventDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
  date: new Date(event.EventDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }),
  rawDate: event.EventDate,
  time: event.EventTime || '10:00 AM',
  location: event.EventLocation || 'City Hall',
  description: event.EventBodyName || 'Committee Meeting',
  committee: event.EventBodyName,
  committeeId: event.EventBodyId,
  comment: event.EventComment,
  topics: event.Topics || [],
  documents: event.Documents || []
});

export const fetchTrackedCommittees = async (): Promise<Committee[]> => {
  try {
    const committees: Committee[] = await fetch(
      `${PROXY_BASE_URL}/committees/tracked`
    ).then(res => res.json());
    return committees;
  } catch (error) {
    console.error('Error fetching tracked committees:', error);
    return [];
  }
};

export interface EventSearchResult {
  items: LegistarEvent[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export const searchEvents = async (
  q: string,
  options: {
    bodyId?: number;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
    searchAttachments?: boolean;
  } = {}
): Promise<EventSearchResult> => {
  const params = new URLSearchParams();
  params.set('q', q);
  if (options.bodyId) params.set('bodyId', options.bodyId.toString());
  if (options.startDate) params.set('startDate', options.startDate);
  if (options.endDate) params.set('endDate', options.endDate);
  if (options.page !== undefined) params.set('page', options.page.toString());
  if (options.pageSize) params.set('pageSize', options.pageSize.toString());
  if (options.searchAttachments) params.set('searchAttachments', 'true');

  const response = await fetch(`${PROXY_BASE_URL}/events/search?${params}`);
  if (!response.ok) {
    throw new Error('Failed to search events');
  }
  return response.json();
};

export const fetchCommitteeMeetings = async (
  page: number = 0,
  startDate?: string,
  endDate?: string,
  committeeId?: number,
  order?: 'asc' | 'desc'
): Promise<Meeting[]> => {
  try {
    // One aggregated request: the backend fetches each committee's page slice,
    // merges + date-sorts them, and inlines each event's matter doc-links — so we
    // no longer fan out a per-committee event fetch + a /items fetch per event
    // (codex PF1). Omitting bodyIds lets the backend default to tracked committees.
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('pageSize', '10');
    if (committeeId) params.set('bodyIds', committeeId.toString());
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    // Only sent for the upcoming view (soonest first); default URLs stay stable
    if (order === 'asc') params.set('order', 'asc');

    const response = await fetch(`${PROXY_BASE_URL}/events?${params}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch meetings (HTTP ${response.status})`);
    }
    const events: LegistarEvent[] = await response.json();
    return events.map(meetingFromEvent);
  } catch (error) {
    console.error('Error fetching meetings:', error);
    throw error;
  }
};